/**
 * @fileoverview
 * Sends the project to the parent frame after a project change: one send for the changes that arrive
 * together, and never while a project is loading.
 */

// The parent saves from the last project it was sent, so a change must not wait longer than this.
const PROJECT_SYNC_DELAY = 200;

/**
 * @param {function} sendProject Sends the current project to the parent frame.
 * @param {function} isLoadingProject Returns true while a project is loading into the VM.
 * @return {{request: function, stop: function}} Call `request` on every project change; the project is sent
 * within the sync delay. Call `stop` when the editor goes away; it leaves no timer behind.
 */
export const makeProjectSync = (sendProject, isLoadingProject) => {
    let timeout = null;
    const sync = () => {
        // The VM is emptied while a project loads, so the sync is held until the whole project is there.
        if (isLoadingProject()) {
            timeout = setTimeout(sync, PROJECT_SYNC_DELAY);
            return;
        }
        timeout = null;
        sendProject();
    };
    return {
        request: () => {
            // A pending sync already covers this change. It is not pushed back, so typing cannot starve the parent.
            if (timeout === null) timeout = setTimeout(sync, PROJECT_SYNC_DELAY);
        },
        stop: () => {
            if (timeout === null) return;
            clearTimeout(timeout);
            timeout = null;
            // A pending change is sent now rather than lost, unless a load has left nothing whole to send.
            if (!isLoadingProject()) sendProject();
        }
    };
};
